import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  Image,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getBrandValue } from "../brandConfig";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const NAVY = "#03177A";
const ORANGE = "#FF6600";

// Placeholder image component
const ImagePlaceholder = ({
  label,
  h = "100%",
}: {
  label: string;
  h?: string | Record<string, string>;
}) => (
  <Box
    w="100%"
    h={h}
    minH="200px"
    bg="linear-gradient(135deg, #E6EEFF 0%, #EBEBEB 100%)"
    borderRadius="xl"
    display="flex"
    alignItems="center"
    justifyContent="center"
    border="1px solid rgba(3, 23, 122, 0.1)"
    overflow="hidden"
  >
    <VStack spacing={2}>
      <Box
        w={12}
        h={12}
        borderRadius="full"
        bg="rgba(3, 23, 122, 0.08)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="xl">📷</Text>
      </Box>
      <Text
        fontSize="sm"
        color="rgba(3, 23, 122, 0.4)"
        fontFamily={getBrandValue("fonts.body")}
      >
        {label}
      </Text>
    </VStack>
  </Box>
);

interface CareCentre {
  name: string;
  description: string;
  rooms: string;
  images: string[];
}

const careCentres: CareCentre[] = [
  {
    name: "Paradise Lakes Care Centre",
    description:
      "Like an oasis in the landscape of elderly care, Paradise Lakes Care Centre has firmly established itself as one of the premier residential aged care providers in South East Queensland.",
    rooms: "40 Rooms Available",
    images: ["Paradise Lakes 1", "Paradise Lakes 2", "Paradise Lakes 3"],
  },
  {
    name: "Vanguard Care Centre",
    description:
      "A modern, purpose-built facility providing exceptional residential aged care in a warm, community-focused environment with comprehensive clinical support.",
    rooms: "60 Rooms Available",
    images: ["Vanguard Centre 1", "Vanguard Centre 2", "Vanguard Centre 3"],
  },
];

const OurFacilities = () => {
  const navigate = useNavigate();
  const facilitiesRef = useRef<HTMLDivElement>(null);
  const [activeImage, setActiveImage] = useState(0);
  const imageRef = useRef<HTMLDivElement>(null);

  const currentCentre = careCentres[0];
  const totalImages = currentCentre.images.length;

  const goToImage = useCallback(
    (index: number) => {
      const newIndex = (index + totalImages) % totalImages;
      if (imageRef.current) {
        gsap.to(imageRef.current, {
          opacity: 0,
          x: index > activeImage ? -30 : 30,
          duration: 0.25,
          ease: "power2.in",
          onComplete: () => {
            setActiveImage(newIndex);
            if (imageRef.current) {
              gsap.fromTo(
                imageRef.current,
                { opacity: 0, x: index > activeImage ? 30 : -30 },
                { opacity: 1, x: 0, duration: 0.35, ease: "power2.out" }
              );
            }
          },
        });
      } else {
        setActiveImage(newIndex);
      }
    },
    [activeImage, totalImages]
  );

  useEffect(() => {
    if (facilitiesRef.current) {
      const sections =
        facilitiesRef.current.querySelectorAll(".facility-section");
      gsap.fromTo(
        sections,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: facilitiesRef.current,
            start: "top 80%",
          },
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <Box bg="#FFFFFF" minHeight="100vh" position="relative">
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        minHeight={{ base: "55vh", md: "75vh" }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        bg={NAVY}
        pt={{ base: "110px", md: "145px", lg: "180px" }}
        pb={{ base: "100px", md: "150px", lg: "200px" }}
      >
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          width={{ base: "300px", md: "500px" }}
          height={{ base: "300px", md: "500px" }}
          borderRadius="full"
          bg="rgba(255, 102, 0, 0.1)"
          filter="blur(60px)"
        />
        <Box
          position="absolute"
          bottom="-30%"
          left="-10%"
          width={{ base: "250px", md: "400px" }}
          height={{ base: "250px", md: "400px" }}
          borderRadius="full"
          bg="rgba(255, 255, 255, 0.05)"
          filter="blur(40px)"
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity={0.03}
          backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
          backgroundSize="50px 50px"
        />

        <Container
          maxW="1200px"
          position="relative"
          zIndex={1}
          px={{ base: 6, md: 8 }}
        >
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
            <Box
              display="inline-block"
              px={4}
              py={2}
              bg="rgba(255, 102, 0, 0.2)"
              borderRadius="full"
              mb={2}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color={ORANGE}
                letterSpacing="0.1em"
                textTransform="uppercase"
                fontFamily={getBrandValue("fonts.body")}
              >
                Residential Aged Care
              </Text>
            </Box>

            <Heading
              as="h1"
              fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="700"
              color="#FFFFFF"
              fontFamily={getBrandValue("fonts.heading")}
              lineHeight="1.1"
            >
              Our Facilities
            </Heading>

            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="rgba(255, 255, 255, 0.8)"
              maxW="700px"
              lineHeight="1.7"
              fontFamily={getBrandValue("fonts.body")}
            >
              Warm, welcoming homes across Queensland where residents can truly
              flourish with dignity, comfort, and personalised care.
            </Text>
          </VStack>
        </Container>

        <Box
          position="absolute"
          bottom="-1px"
          left="0"
          right="0"
          height={{ base: "60px", md: "100px" }}
          overflow="hidden"
        >
          <svg
            viewBox="0 0 1440 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: "100%", height: "100%", display: "block" }}
            preserveAspectRatio="none"
          >
            <path
              d="M0 100 L0 50 Q720 -50 1440 50 L1440 100 Z"
              fill="#FFFFFF"
            />
          </svg>
        </Box>
      </Box>

      {/* Care Centres Carousel Section */}
      <Box
        ref={facilitiesRef}
        className="facility-section"
        bg="#FFFFFF"
        py={{ base: 16, md: 20, lg: 28 }}
      >
        <Container maxW="1200px" px={{ base: 6, md: 8 }}>
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 8, lg: 16 }}
            align="center"
          >
            {/* Left: Text Content */}
            <Box flex="1" w="100%">
              <VStack align="start" spacing={5}>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="600"
                  color={NAVY}
                  fontFamily={getBrandValue("fonts.heading")}
                >
                  Residential Aged Care
                </Text>

                <Heading
                  as="h2"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="700"
                  color={NAVY}
                  fontFamily={getBrandValue("fonts.heading")}
                  lineHeight="1.1"
                >
                  Our Facilities
                </Heading>

                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="600"
                  color={NAVY}
                  fontFamily={getBrandValue("fonts.heading")}
                  pt={2}
                >
                  {currentCentre.name}
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="rgba(3, 23, 122, 0.7)"
                  lineHeight="1.8"
                  fontFamily={getBrandValue("fonts.body")}
                >
                  {currentCentre.description}
                </Text>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontWeight="600"
                  color={ORANGE}
                  fontFamily={getBrandValue("fonts.body")}
                >
                  {currentCentre.rooms}
                </Text>

                {/* Find out more button */}
                <Box
                  as="button"
                  position="relative"
                  display="inline-flex"
                  alignItems="center"
                  bg="transparent"
                  pl={0}
                  pr={4}
                  py={2}
                  h="clamp(40px, 3.5vw, 48px)"
                  transition="all 0.3s ease"
                  cursor="pointer"
                  border="none"
                  role="group"
                  _hover={{ transform: "translateY(-2px)" }}
                  onClick={() => {
                    navigate("/contact-us");
                    window.scrollTo(0, 0);
                  }}
                >
                  <Box
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                    w="clamp(30px, 2.5vw, 36px)"
                    h="clamp(30px, 2.5vw, 36px)"
                    borderRadius="full"
                    bg={ORANGE}
                    transition="all 0.4s ease"
                    zIndex={0}
                    _groupHover={{
                      w: "100%",
                      borderRadius: "full",
                    }}
                  />
                  <Text
                    color={NAVY}
                    fontSize="clamp(0.9rem, 1.3vw, 1.1rem)"
                    fontWeight="700"
                    fontFamily={getBrandValue("fonts.body")}
                    position="relative"
                    zIndex={1}
                    pl="clamp(10px, 1vw, 12px)"
                    _groupHover={{ color: "#FFFFFF" }}
                    transition="color 0.4s ease"
                  >
                    Find out more
                  </Text>
                </Box>
              </VStack>
            </Box>

            {/* Right: Image Carousel */}
            <Box flex="1" w="100%" position="relative">
              <Flex align="center" gap={4}>
                {/* Left Arrow */}
                <Box
                  as="button"
                  border="none"
                  bg="transparent"
                  cursor="pointer"
                  p={0}
                  transition="all 0.3s ease"
                  _hover={{ transform: "scale(1.15)" }}
                  onClick={() => goToImage(activeImage - 1)}
                  display={{ base: "none", md: "block" }}
                  flexShrink={0}
                >
                  <Image
                    src="/icons/VC_Icons_Arrow Left.png"
                    alt="Previous"
                    w="40px"
                    h="40px"
                  />
                </Box>

                {/* Image */}
                <Box flex="1" ref={imageRef}>
                  <ImagePlaceholder
                    label={currentCentre.images[activeImage]}
                    h={{ base: "280px", md: "360px", lg: "420px" }}
                  />
                </Box>

                {/* Right Arrow */}
                <Box
                  as="button"
                  border="none"
                  bg="transparent"
                  cursor="pointer"
                  p={0}
                  transition="all 0.3s ease"
                  _hover={{ transform: "scale(1.15)" }}
                  onClick={() => goToImage(activeImage + 1)}
                  display={{ base: "none", md: "block" }}
                  flexShrink={0}
                >
                  <Image
                    src="/icons/VC_Icons_Arrow Right.png"
                    alt="Next"
                    w="40px"
                    h="40px"
                  />
                </Box>
              </Flex>

              {/* Mobile Arrows */}
              <HStack
                justify="center"
                spacing={6}
                mt={4}
                display={{ base: "flex", md: "none" }}
              >
                <Box
                  as="button"
                  border="none"
                  bg="transparent"
                  cursor="pointer"
                  p={0}
                  onClick={() => goToImage(activeImage - 1)}
                >
                  <Image
                    src="/icons/VC_Icons_Arrow Left.png"
                    alt="Previous"
                    w="32px"
                    h="32px"
                  />
                </Box>
                <Box
                  as="button"
                  border="none"
                  bg="transparent"
                  cursor="pointer"
                  p={0}
                  onClick={() => goToImage(activeImage + 1)}
                >
                  <Image
                    src="/icons/VC_Icons_Arrow Right.png"
                    alt="Next"
                    w="32px"
                    h="32px"
                  />
                </Box>
              </HStack>

              {/* Dot Indicators */}
              <HStack justify="center" spacing={2} mt={6}>
                {currentCentre.images.map((_, index) => (
                  <Box
                    key={index}
                    w={3}
                    h={3}
                    borderRadius="full"
                    bg={index === activeImage ? ORANGE : "transparent"}
                    border="2px solid"
                    borderColor={
                      index === activeImage ? ORANGE : "rgba(3, 23, 122, 0.3)"
                    }
                    cursor="pointer"
                    transition="all 0.3s ease"
                    onClick={() => goToImage(index)}
                    _hover={{ borderColor: ORANGE }}
                  />
                ))}
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        bg="#FFFFFF"
        position="relative"
        py={{ base: 16, md: 20, lg: 24 }}
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-50%"
          right="-5%"
          width="300px"
          height="300px"
          borderRadius="full"
          bg="rgba(255, 102, 0, 0.05)"
          filter="blur(40px)"
        />
        <Box
          position="absolute"
          bottom="-50%"
          left="-5%"
          width="250px"
          height="250px"
          borderRadius="full"
          bg="rgba(3, 23, 122, 0.05)"
          filter="blur(40px)"
        />

        <Container
          maxW="800px"
          position="relative"
          zIndex={1}
          px={{ base: 6, md: 8 }}
        >
          <VStack spacing={6} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="700"
              color={NAVY}
              fontFamily={getBrandValue("fonts.heading")}
            >
              Arrange a Visit
            </Heading>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="rgba(3, 23, 122, 0.7)"
              lineHeight="1.8"
              fontFamily={getBrandValue("fonts.body")}
              maxW="600px"
            >
              We warmly welcome families to tour our facilities, meet our care
              team, and see firsthand the quality of life our residents enjoy.
              Get in touch to arrange a personalised visit.
            </Text>
            <Box
              as="button"
              mt={4}
              px={10}
              py={4}
              bg={ORANGE}
              color="#FFFFFF"
              borderRadius="full"
              fontSize="lg"
              fontWeight="600"
              fontFamily={getBrandValue("fonts.body")}
              border="none"
              cursor="pointer"
              transition="all 0.3s ease"
              _hover={{
                bg: "#E55A00",
                transform: "translateY(-3px)",
                boxShadow: "0 10px 30px rgba(255, 102, 0, 0.3)",
              }}
              _active={{
                transform: "translateY(0px)",
              }}
              onClick={() => {
                navigate("/contact-us");
                window.scrollTo(0, 0);
              }}
            >
              Contact Us to Arrange a Visit
            </Box>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default OurFacilities;
